import React, { useState } from 'react';
import { useAuth } from '../AuthProvider';
import { baseUrl_SPB } from '../BaseUrl';
import { TextField, Button, Paper, Typography, Alert, Stack, Checkbox, FormControlLabel } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const initialForm = {
  itemName: '',
  itemDescription: '',
  itemCategory: '',
  itemNumber: '',
  sku: '',
  barcode: '',
  qrCode: '',
  binCode: '',
  price: '',
  quantity: '',
  supplierName: '',
  note: '',
  purchasePrice: '',
  isMaterial: 'Y', // Initialized as 'Y'
};

const AddInventory = () => {
  const authContext = useAuth();
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const validate = () => {
    // Base validations mandatory for both isMaterial and Labor
    if (!form.itemName) return 'Item name is mandatory';
    if (!form.itemDescription) return 'Item description is mandatory';
    if (!form.itemCategory) return 'Item category is mandatory';
    if (!form.itemNumber) return 'Item number is mandatory';
    if (!form.price || isNaN(form.price)) return 'Price is mandatory and must be a valid number';

    // Conditional validations only enforced if it is a isMaterial ('Y')
    if (form.isMaterial === 'Y') {
      if (!form.sku) return 'SKU is mandatory';
      if (!form.barcode) return 'Barcode is mandatory';
      if (!form.qrCode) return 'QR code is mandatory';
      if (!form.binCode) return 'Bin code is mandatory';
      
      const binCodeRegex = /^[a-zA-Z0-9\-]{3,20}$/;
      if (!binCodeRegex.test(form.binCode)) return 'Bin code must be 3-20 characters, alphanumeric or hyphen only';
      
      if (!form.quantity || isNaN(form.quantity)) return 'Quantity is mandatory and must be a valid number';
      if (!form.supplierName) return 'Supplier name is mandatory';
    }

    return null;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.checked ? 'Y' : 'N' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      return;
    }
    
    setLoading(true);
    try {
      const payload = { ...form, id: uuidv4() };
      const response = await fetch(`${baseUrl_SPB}/api/v1/inventory/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to save inventory');
      setSuccess('Inventory item added successfully!');
      setForm(initialForm);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 2, maxWidth: 600, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>Add New Inventory Item</Typography>
      <form onSubmit={handleSubmit} autoComplete="off">
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          
          {/* isMaterial Checkbox placed at the very top */}
          <FormControlLabel
            control={
              <Checkbox
                name="isMaterial"
                checked={form.isMaterial === 'Y'}
                onChange={handleCheckboxChange}
                color="primary"
              />
            }
            label="Material"
          />

          {/* Form items visible to both Material and Labor workflows */}
          <TextField label="Item Name" name="itemName" value={form.itemName} onChange={handleChange} required fullWidth />
          <TextField label="Description" name="itemDescription" value={form.itemDescription} onChange={handleChange} required fullWidth />
          <TextField label="Category" name="itemCategory" value={form.itemCategory} onChange={handleChange} required fullWidth />
          <TextField label="Item Number" name="itemNumber" value={form.itemNumber} onChange={handleChange} required fullWidth />
          <TextField label="Price" name="price" value={form.price} onChange={handleChange} required fullWidth type="number" />
          <TextField label="Purchase Price" name="purchasePrice" value={form.purchasePrice} onChange={handleChange} fullWidth type="number" />
          
          {/* Form items conditionally rendered only when isMaterial is 'Y' */}
          {form.isMaterial === 'Y' && (
            <>
              <TextField label="SKU" name="sku" value={form.sku} onChange={handleChange} required fullWidth />
              <TextField label="Barcode" name="barcode" value={form.barcode} onChange={handleChange} required fullWidth />
              <TextField label="QR Code" name="qrCode" value={form.qrCode} onChange={handleChange} required fullWidth />
              <TextField label="Bin Code" name="binCode" value={form.binCode} onChange={handleChange} required fullWidth />
              <TextField label="Quantity" name="quantity" value={form.quantity} onChange={handleChange} required fullWidth type="number" />
              <TextField label="Supplier Name" name="supplierName" value={form.supplierName} onChange={handleChange} required fullWidth />
            </>
          )}

          <TextField label="Note" name="note" value={form.note} onChange={handleChange} fullWidth />
          
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Item'}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};

export default AddInventory;